import { useEffect, useState } from "react";
import { db } from "@/os/data";
import type { AdminInsights } from "@/os/data/types";
import { useEnvStore } from "@/os/env/useEnvStore";
import { useDataFilterStore } from "@/os/settings/useDataFilterStore";

/**
 * Module-level cache so the dashboard and the sidebar share a single fetch
 * per env. Refetched on env change or after the TTL expires.
 */
let cache: { env: string | null; at: number; data: AdminInsights | null } = {
  env: null,
  at: 0,
  data: null,
};
const listeners = new Set<(d: AdminInsights | null) => void>();
const TTL = 60_000;
let inflight: Promise<AdminInsights> | null = null;

async function load(cacheKey: string, force = false): Promise<AdminInsights> {
  if (!force && cache.data && cache.env === cacheKey && Date.now() - cache.at < TTL) {
    return cache.data;
  }
  if (inflight) return inflight;
  inflight = db()
    .adminInsights()
    .then((d) => {
      cache = { env: cacheKey, at: Date.now(), data: d };
      listeners.forEach((fn) => fn(d));
      return d;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

export function useAdminInsights(enabled = true) {
  const env = useEnvStore((s) => s.env) ?? "none";
  const [filter, setFilter] = useState(() => {
    const s = useDataFilterStore.getState();
    return { showSimulated: s.showSimulated, activeYear: s.activeYear };
  });
  useEffect(() => {
    return useDataFilterStore.subscribe((s) =>
      setFilter({ showSimulated: s.showSimulated, activeYear: s.activeYear }),
    );
  }, []);
  const cacheKey = `${env}|${filter.showSimulated ? 1 : 0}|${filter.activeYear}`;
  const [data, setData] = useState<AdminInsights | null>(() =>
    cache.env === cacheKey ? cache.data : null,
  );
  const [loading, setLoading] = useState(enabled && !data);

  useEffect(() => {
    if (!enabled) return;
    const fn = (d: AdminInsights | null) => setData(d);
    listeners.add(fn);
    setLoading(!cache.data || cache.env !== cacheKey);
    load(cacheKey)
      .then((d) => setData(d))
      .finally(() => setLoading(false));
    return () => {
      listeners.delete(fn);
    };
  }, [enabled, cacheKey]);

  return {
    data,
    loading,
    refresh: () => load(cacheKey, true).then((d) => setData(d)),
  };
}